import React from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, Play, SkipForward, Square, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg, kApi } from '../../lib/karaokeApi';
import { StatusPill } from './RequestsPanel';

const run = async (fn, ok, onChanged) => {
  try { await fn(); if (ok) toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e)); }
};

const Btn = ({ onClick, children, cls = 'bg-slate-100 text-slate-800', testId, label }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} className={`h-10 min-w-10 px-3 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-1 ${cls}`} data-testid={testId}>{children}</button>
);

// Ready Playlist: queue (reorder/remove/start), now playing, and completed history.
export const PlaylistPanel = ({ playlist = [], nextId, canControl, onChanged }) => {
  const queue = playlist.filter((p) => p.status === 'ready' || p.status === 'playing');
  const done = playlist.filter((p) => p.status === 'played' || p.status === 'skipped');
  const ready = queue.filter((p) => p.status === 'ready');
  const move = (id, dir) => {
    const ids = ready.map((p) => p.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(() => kApi.reorder([...queue.filter((p) => p.status === 'playing').map((p) => p.id), ...ids]), null, onChanged);
  };
  return (
    <div className="space-y-6">
      <div className="space-y-2" data-testid="karaoke-playlist">
        {!queue.length && <p className="text-sm text-slate-500 py-6 text-center">The Ready Playlist is empty. Add songs from Guest Requests.</p>}
        {queue.map((p, i) => {
          const playing = p.status === 'playing';
          const status = playing ? 'playing' : p.id === nextId ? 'up_next' : 'ready';
          return (
            <div key={p.id} className={`rounded-2xl border p-3 flex flex-wrap items-center gap-3 ${playing ? 'border-fuchsia-400 bg-fuchsia-50' : 'border-slate-200 bg-white'}`} data-testid={`karaoke-playlist-item-${p.id}`}>
              <span className="w-7 text-center font-black text-slate-400">{i + 1}</span>
              <img src={p.thumbnail} alt="" className="w-20 h-12 rounded-lg object-cover shrink-0" />
              <div className="min-w-0 flex-1 basis-40">
                <p className="font-bold text-slate-900 break-words">{p.title || 'Untitled video'}</p>
                <p className="text-xs text-slate-600">{p.guest_name}{p.room_number ? ` · Room ${p.room_number}` : ''} · song {p.song_idx}</p>
                {p.error && <p className="text-xs text-rose-700 font-semibold flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3.5 h-3.5" /> {p.error} — open on YouTube or mark Played/Skipped</p>}
              </div>
              <StatusPill status={status} testId={`karaoke-playlist-status-${p.id}`} />
              {canControl && (
                <div className="flex flex-wrap gap-1.5 w-full sm:w-auto justify-end">
                  {!playing && <>
                    <Btn label="Move up" onClick={() => move(p.id, -1)} testId={`karaoke-up-${p.id}`}><ArrowUp className="w-4 h-4" /></Btn>
                    <Btn label="Move down" onClick={() => move(p.id, 1)} testId={`karaoke-down-${p.id}`}><ArrowDown className="w-4 h-4" /></Btn>
                    <Btn label="Remove" cls="bg-rose-50 text-rose-700" onClick={() => run(() => kApi.remove(p.id), 'Removed — the request can be added again', onChanged)} testId={`karaoke-remove-${p.id}`}><Trash2 className="w-4 h-4" /></Btn>
                    <Btn label="Start" cls="bg-fuchsia-600 text-white" onClick={() => run(() => kApi.start(p.id), 'Playing on the projector', onChanged)} testId={`karaoke-start-${p.id}`}><Play className="w-4 h-4" /> Start</Btn>
                  </>}
                  {playing && <Btn label="Stop" cls="bg-slate-800 text-white" onClick={() => run(() => kApi.stop(), 'Stopped — song stays in the playlist', onChanged)} testId={`karaoke-stop-${p.id}`}><Square className="w-4 h-4" /> Stop</Btn>}
                  <Btn label="Mark played" cls="bg-emerald-50 text-emerald-700" onClick={() => run(() => kApi.mark(p.id, 'played'), 'Marked as played', onChanged)} testId={`karaoke-mark-played-${p.id}`}><CheckCircle2 className="w-4 h-4" /> Played</Btn>
                  <Btn label="Skip" cls="bg-amber-50 text-amber-800" onClick={() => run(() => kApi.mark(p.id, 'skipped'), 'Skipped', onChanged)} testId={`karaoke-mark-skipped-${p.id}`}><SkipForward className="w-4 h-4" /> Skip</Btn>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {done.length > 0 && (
        <div data-testid="karaoke-history">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-2">Completed ({done.length})</h3>
          <div className="space-y-1.5">
            {[...done].sort((a, b) => (b.finished_at || '').localeCompare(a.finished_at || '')).map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-2">
                <p className="flex-1 min-w-0 text-sm text-slate-700 break-words"><b>{p.guest_name}</b> — {p.title || 'Untitled video'}</p>
                <span className="text-xs text-slate-500">{p.finished_at ? new Date(p.finished_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                <StatusPill status={p.status} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistPanel;
